import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { validateText } from '../../../utils/text-validation';
import { validateUrl } from '../../../utils/url-validation';
import { validateFile } from '../../../utils/file-validation';
import type { InputType } from '../types';
import { INPUT_TYPES } from '../utils';
import {
  TypeSelector, TypeButton,
  InputArea, TextInput, UrlInputWrapper, UrlInput, UrlHint,
  FileDropZone, FileSelected, FileName, FileSize, RemoveFileBtn,
  DropPlaceholder, DropLabel, DropHint, FileErrorMsg,
  InputFooter, CharCount, AnalyzeBtn, Spinner,
} from '../styles';

interface Props {
  inputType: InputType;
  onTypeChange: (t: InputType) => void;
  inputText: string;
  onTextChange: (s: string) => void;
  inputUrl: string;
  onUrlChange: (s: string) => void;
  selectedFile: File | null;
  fileError: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  isRunning: boolean;
  statusMsg?: string;
  onAnalyze: () => void;
}

export const InputForm: React.FC<Props> = ({
  inputType, onTypeChange, inputText, onTextChange, inputUrl, onUrlChange,
  selectedFile, fileError, fileInputRef, onFileSelect, onRemoveFile,
  isRunning, statusMsg, onAnalyze,
}) => {
  const { t } = useTranslation();
  const textValidation = useMemo(() => validateText(inputText), [inputText]);
  const urlValidation = useMemo(() => validateUrl(inputUrl), [inputUrl]);

  const canAnalyze = inputType === 'text'
    ? textValidation.valid
    : inputType === 'url'
      ? urlValidation.valid
      : selectedFile !== null;

  const charCountStyle =
    inputType === 'text'
      ? { color: textValidation.level === 'error' ? '#ef4444' : textValidation.level === 'warning' ? '#eab308' : '#22c55e' }
      : inputType === 'url' && urlValidation.error
        ? { color: '#ef4444' }
        : inputType === 'url' && urlValidation.valid
          ? { color: '#22c55e' }
          : undefined;

  const charCountText = inputType === 'text'
    ? (textValidation.error || textValidation.warning || `${inputText.trim().length} characters`)
    : inputType === 'url'
      ? (urlValidation.error || (inputUrl.trim() ? t('common.validUrl') : t('common.enterUrl')))
      : selectedFile ? t('common.ready') : t('common.noFileSelected');

  return (
    <>
      <TypeSelector>
        {INPUT_TYPES.map(({ key, labelKey }) => (
          <TypeButton key={key} active={inputType === key} onClick={() => onTypeChange(key)}>
            {t(labelKey)}
          </TypeButton>
        ))}
      </TypeSelector>

      <InputArea>
        {inputType === 'text' ? (
          <TextInput
            placeholder={t('pipeline.textPlaceholder')}
            value={inputText}
            onChange={e => onTextChange(e.target.value)}
            disabled={isRunning}
          />
        ) : inputType === 'url' ? (
          <UrlInputWrapper>
            <UrlInput
              type="url"
              placeholder={t('pipeline.urlPlaceholder')}
              value={inputUrl}
              onChange={e => onUrlChange(e.target.value)}
              disabled={isRunning}
            />
            <UrlHint>{t('pipeline.urlHint')}</UrlHint>
          </UrlInputWrapper>
        ) : (
          <FileDropZone>
            <input
              ref={fileInputRef}
              type="file"
              accept={INPUT_TYPES.find(t => t.key === inputType)?.accept}
              onChange={onFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <FileSelected>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</FileSize>
                <RemoveFileBtn onClick={onRemoveFile}>Remove</RemoveFileBtn>
              </FileSelected>
            ) : (
              <DropPlaceholder onClick={() => fileInputRef.current?.click()}>
                <DropLabel>Click to select {inputType} file</DropLabel>
                <DropHint>
                  {inputType === 'image' && t('pipeline.imageHint')}
                  {inputType === 'audio' && t('pipeline.audioHint')}
                  {inputType === 'video' && t('pipeline.videoHint')}
                </DropHint>
              </DropPlaceholder>
            )}
            {fileError && <FileErrorMsg>{fileError}</FileErrorMsg>}
          </FileDropZone>
        )}
        <InputFooter>
          <CharCount style={charCountStyle}>{charCountText}</CharCount>
          <AnalyzeBtn onClick={onAnalyze} disabled={isRunning || !canAnalyze}>
            {isRunning ? (<><Spinner />{statusMsg || t('common.analyzing')}</>) : t('pipeline.runFullAnalysis')}
          </AnalyzeBtn>
        </InputFooter>
      </InputArea>
    </>
  );
};
